import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '../../ui/Button';
import Tooltip from '../../ui/Tooltip';
import FilterCard from './FilterCard';
import useFocusTrap from './useFocusTrap';
import { countActive, isFilterEmpty } from './registry';

// A screen that marks no field `defaultExpanded` opens its first few cards.
const FALLBACK_EXPANDED = 2;
const FIELD_SEARCH_THRESHOLD = 5;

/**
 * Right-anchored slide-out filter panel (full width on phones) over a dimmed overlay.
 * aria-modal dialog: focus is trapped, Escape closes, focus returns to the opener.
 * Changes apply as they are made; "Done" just closes. `focusKey` opens the panel at one
 * field (a chip was clicked).
 */
const FilterPanel = ({ open, onClose, fields, values, onChange, onClearAll, focusKey, returnFocusRef }) => {
    const panelRef = useRef(null);
    const searchRef = useRef(null);
    const cardRefs = useRef({});
    const [query, setQuery] = useState('');
    useFocusTrap(open, panelRef, onClose, returnFocusRef);

    useEffect(() => {
        if (!open) return;
        setQuery('');
        // Initial focus: the chip's field, else the field search, else the first card.
        const id = requestAnimationFrame(() => {
            const target =
                (focusKey && cardRefs.current[focusKey]) ||
                searchRef.current ||
                cardRefs.current[fields[0]?.key] ||
                panelRef.current;
            target?.focus?.();
            if (focusKey) target?.scrollIntoView?.({ block: 'nearest' });
        });
        return () => cancelAnimationFrame(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, focusKey]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return needle ? fields.filter((f) => f.label.toLowerCase().includes(needle)) : fields;
    }, [fields, query]);

    if (!open) return null;

    const active = countActive(fields, values);
    const declared = fields.some((f) => f.defaultExpanded);
    const startsOpen = (field, index) =>
        field.key === focusKey ||
        !isFilterEmpty(field, values[field.key]) ||
        (declared ? Boolean(field.defaultExpanded) : index < FALLBACK_EXPANDED);

    return createPortal(
        <div className="fixed inset-0 z-50">
            <div
                className="absolute inset-0 bg-slate-900/40 motion-safe:animate-ff-fade-in"
                onClick={onClose}
                aria-hidden="true"
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="ff-panel-title"
                tabIndex={-1}
                className="absolute inset-y-0 right-0 flex w-full flex-col bg-slate-50 shadow-2xl outline-none sm:max-w-sm sm:border-l sm:border-slate-200 motion-safe:animate-ff-slide-in"
            >
                <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
                    <div className="flex items-baseline gap-2">
                        <h2 id="ff-panel-title" className="text-base font-semibold text-slate-900">
                            Filters
                        </h2>
                        <span className="text-sm text-slate-500" aria-live="polite">
                            {active === 0 ? 'None applied' : `${active} applied`}
                        </span>
                    </div>
                    <Tooltip label="Close filters">
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close filters"
                            className="grid h-8 w-8 place-content-center rounded-md text-slate-500 outline-none hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-2 focus-visible:ring-primary-300"
                        >
                            <X className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </Tooltip>
                </header>

                {fields.length > FIELD_SEARCH_THRESHOLD && (
                    <div className="border-b border-slate-200 bg-white px-4 py-2">
                        <input
                            ref={searchRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search filters…"
                            aria-label="Search filters"
                            className="h-9 w-full rounded-md border border-slate-200 bg-white px-2.5 text-sm shadow-sm outline-none placeholder:text-slate-400 focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-200"
                        />
                    </div>
                )}

                <div className="flex-1 space-y-2 overflow-y-auto overscroll-contain p-3">
                    {visible.length === 0 ? (
                        <p className="px-1 py-6 text-center text-sm text-slate-500">
                            {fields.length === 0 ? 'No filters available.' : 'No filters match your search.'}
                        </p>
                    ) : (
                        visible.map((field, index) => (
                            <FilterCard
                                key={field.key}
                                ref={(el) => {
                                    cardRefs.current[field.key] = el;
                                }}
                                field={field}
                                value={values[field.key]}
                                onChange={(next) => onChange(field.key, next)}
                                onClear={() => onChange(field.key, undefined)}
                                defaultExpanded={startsOpen(field, fields.indexOf(field) ?? index)}
                            />
                        ))
                    )}
                </div>

                <footer className="flex items-center justify-between gap-2 border-t border-slate-200 bg-white px-4 py-3">
                    <Button variant="ghost" onClick={onClearAll} disabled={active === 0}>
                        Clear all
                    </Button>
                    <Button onClick={onClose}>Done</Button>
                </footer>
            </div>
        </div>,
        document.body
    );
};

export default FilterPanel;
